import { ChevronCloseDrawer } from 'assets/icons';
import CloseIcon from 'assets/images/actions/closeIcon.svg?react';
import FullArrowRight from 'assets/images/arrows/fullArrowRight.svg?react';
import cn from 'classnames';
import { motion, PanInfo, useAnimation, Variants } from 'framer-motion';
import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { handleAddOnlyOverflow, handleAddOverflow, handleRemoveOnlyOverflow, handleRemoveOverflow, isMobile } from 'shared/util/utility';
import './styles.scss';

interface BottomDrawerProps {
	isBottomDrawer: any;
	setIsBottomDrawer?: Dispatch<SetStateAction<boolean>>;
	handleBottomDrawer: () => void;
	children?: React.ReactElement;
	className?: string;
	cyCloseModalRef?: string;
	cyModalActionRef?: string;
	drawerBackground?: string;
	heading?: string;
	headerAction?: any;
	lockOnlyOverflow?: boolean;
	isDrawerHeader?: boolean;
	isDrawerClose?: boolean;
	isDrawerPrevious?: boolean;
	hideGrabber?: boolean;
	usePortal?: boolean;
}

const BottomDrawer: React.FC<BottomDrawerProps> = ({
	isBottomDrawer,
	children,
	handleBottomDrawer,
	className,
	cyCloseModalRef = '',
	drawerBackground = '',
	heading,
	headerAction,
	isDrawerHeader,
	lockOnlyOverflow,
	cyModalActionRef,
	isDrawerPrevious = false,
	isDrawerClose = true,
	hideGrabber = false,
	usePortal = false,
}) => {
	const controls = useAnimation();
	const initialTouchY = useRef<number | null>(null);
	const initialTouchOnGrabber = useRef<boolean>(false);
	const velocityThreshold = 300;
	const offsetYToTravel = 40;

	const [isDraggable, setIsDraggable] = useState(true);
	useEffect(() => {
		controls.start(isBottomDrawer ? 'visible' : 'hidden');
		if (isMobile() && isBottomDrawer) {
			lockOnlyOverflow ? handleAddOnlyOverflow() : handleAddOverflow();
		}
		return () => {
			lockOnlyOverflow ? handleRemoveOnlyOverflow() : handleRemoveOverflow();
		};
	}, [isBottomDrawer, controls]);

	const variants: Variants = {
		visible: {
			y: 0,
			opacity: 1,
		},
		hidden: {
			y: '100%',
			opacity: 0,
			transition: {
				duration: 0.2,
			},
		},
	};

	const onDragStart = (e: any) => {
		const target = e.target;
		const classesToCheckForDrag = ['bottomDrawer__content__grabber', 'bottomDrawer__content__grabber__thumb'];
		const isGrabber = classesToCheckForDrag.some((className) => target.classList.contains(className));

		setIsDraggable(isGrabber);
		if (isGrabber) {
			initialTouchY.current = e.clientY || e.touches[0].clientY;
			initialTouchOnGrabber.current = true;
		}
	};

	const onDrag = (e: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
		if (initialTouchY.current && initialTouchOnGrabber.current) {
			const dragDistance = info.point.y - initialTouchY.current;

			if (dragDistance > 100) {
				controls.start({ y: dragDistance });
			}
		}
	};

	const onDragEnd = (e: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
		const yDrag = Math.abs(info.offset.y) > Math.abs(info.offset.x);
		if (initialTouchY.current && initialTouchOnGrabber.current && yDrag) {
			if (info.velocity.y > velocityThreshold || info.offset.y > offsetYToTravel) {
				controls.start('hidden');
				setTimeout(() => handleBottomDrawer(), 200);
			} else {
				controls.start('visible');
			}
		}
		initialTouchY.current = null;
		initialTouchOnGrabber.current = false;
		setIsDraggable(true);
	};

	const preventDefault = (e: any) => {
		e.preventDefault();
	};

	const drawerContent = (
		<div className={`bottomDrawer bottomDrawer--${drawerBackground}`}>
			<div
				className={cn('bottomDrawer__backdrop', {
					bottomDrawer__backdrop__visible: isBottomDrawer,
				})}
				onClick={handleBottomDrawer}
			/>
			<motion.div
				className={`${cn('bottomDrawer__content', `bottomDrawer__content--${drawerBackground}`, {
					'bottomDrawer__content--visible': isBottomDrawer,
				})} ${className}`}
				style={{ overflow: isBottomDrawer ? 'hidden' : 'auto' }}
				initial="hidden"
				animate={controls}
				variants={variants}
				transition={{
					stiffness: 300,
					damping: 25,
					duration: 0.2,
					ease: 'easeOut',
				}}
				dragMomentum={true}
				dragElastic={0.1}
				dragListener={!!isMobile()}
				dragConstraints={{ top: 0, bottom: 0, left: 0, right: 0 }}
				drag={isDraggable ? 'y' : false}
				onDragStart={onDragStart}
				onDragEnd={onDragEnd}
				onDrag={onDrag}
				onTouchMove={preventDefault}
			>
				{!hideGrabber && (
					<div className="bottomDrawer__content__grabber">
						<div className="bottomDrawer__content__grabber__thumb" />
					</div>
				)}

				{isDrawerHeader && (
					<div className="bottomDrawer__content__contentHeading">
						<ChevronCloseDrawer onClick={handleBottomDrawer} />
						<span className="bottomDrawer__content__title">{heading}</span>
						<div data-cy={cyModalActionRef}>{headerAction}</div>
					</div>
				)}
				{isDrawerPrevious && (
					<div className="bottomDrawer__content__previous">
						<FullArrowRight />
					</div>
				)}
				{isDrawerClose && (
					<div className="bottomDrawer__content__close" onClick={handleBottomDrawer} data-cy={cyCloseModalRef}>
						<CloseIcon />
					</div>
				)}
				<div>{children}</div>
			</motion.div>
		</div>
	);

	if (usePortal) {
		const root = document.getElementById('root');
		if (!root) return null;
		return ReactDOM.createPortal(drawerContent, root);
	}

	return drawerContent;
};

export default BottomDrawer;
